import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import OrderEditor from './OrderEditor';
import { supabase } from '@/lib/supabase';
import { Trash2, Edit, Filter, Search, Loader2, X, Calendar, History } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { handleDeleteOrder } from '@/api/orders';
import { formatSwedishDateTime } from '@/utils/swedishLocalization';
import SwedishDateDisplay from '@/components/SwedishDateDisplay';
import { fetchDeliveryStartDate, fetchDeliverySlots, calculateAvailableDeliveryDates, DeliveryDateOption } from '@/lib/deliveryUtils';

interface CustomerProfile {
  id: string;
  first_name: string | any;
  last_name: string | any;
  email: string | any;
  address: string | any;
  phone?: string | any;
}

interface Order {
  id: string;
  display_id?: number;
  created_at: string;
  status: string;
  total_amount: number;
  customer_id?: string;
  guest_email?: string;
  guest_details?: any;
  delivery_date?: string;
  profiles: CustomerProfile | null;
}

const OrderList: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [deleteOrderId, setDeleteOrderId] = useState<string | null>(null);
  
  // Replace individual date filters with dropdown options
  const [deliveryDateOptions, setDeliveryDateOptions] = useState<DeliveryDateOption[]>([]);
  const [selectedDeliveryDateFilter, setSelectedDeliveryDateFilter] = useState<string>('');
  const [historicalDateFilter, setHistoricalDateFilter] = useState<string>('');
  
  const { t } = useTranslation();

  // Load delivery date options when component mounts
  useEffect(() => {
    loadDeliveryDateOptions();
  }, []);

  useEffect(() => {
    checkAuth();
    checkDatabaseSchema();
    fetchOrders();
  }, [statusFilter, selectedDeliveryDateFilter, historicalDateFilter]);

  const loadDeliveryDateOptions = async () => {
    try {
      const startDate = await fetchDeliveryStartDate();
      const slots = await fetchDeliverySlots();
      const availableDates = calculateAvailableDeliveryDates(startDate, slots);
      setDeliveryDateOptions(availableDates);
    } catch (error) {
      console.error('Error loading delivery dates:', error);
      toast.error('Kunde inte ladda leveransdatum');
    }
  };

  const checkDatabaseSchema = async () => {
    console.log('Checking database schema...');
    try {
      // Check if the orders table exists and its columns using a simpler approach
      const { data, error } = await supabase
        .from('orders')
        .select('id')
        .limit(1);
      
      console.log('Orders table check result:', { data, error });
      
      if (error) {
        console.error('Error checking orders table:', error);
        
        if (error.code === '42P01') {
          console.error('Orders table does not exist!');
        } else if (error.code === '42703') {
          console.error('Column in query does not exist!');
        }
        return;
      }
      
      console.log('Orders table exists and is accessible');
    } catch (error) {
      console.error('Error checking database schema:', error);
    }
  };

  const checkAuth = async () => {
    console.log('Checking authentication status...');
    const { data, error } = await supabase.auth.getSession();
    console.log('Auth session:', data, error);
    
    if (error) {
      console.error('Auth error:', error);
    }
    
    if (!data.session) {
      console.log('No active session - user not logged in');
    }
  };

  const fetchOrders = async () => {
    setIsLoading(true);
    try {
      console.log('Fetching orders...');
      // Use a single query with proper foreign key relationship
      let query = supabase
        .from('orders')
        .select(`
          id, 
          display_id,
          created_at, 
          status, 
          total_amount,
          customer_id,
          guest_email,
          guest_details,
          delivery_date,
          order_items (
            id,
            quantity,
            price_at_time,
            product:products (
              id,
              name,
              price
            )
          ),
          profiles:profiles(
            id, 
            first_name, 
            last_name, 
            email, 
            phone,
            address
          )
        `)
        .order('created_at', { ascending: false });
      
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }
      
      if (selectedDeliveryDateFilter) {
        query = query.eq('delivery_date', selectedDeliveryDateFilter);
      }
      
      if (historicalDateFilter) {
        query = query.eq('delivery_date', historicalDateFilter);
      }
      
      const { data, error } = await query;
      
      console.log('Orders response:', { data, error });
      
      if (error) throw error;
      
      // Process the data to handle both profile-linked orders and guest orders
      const processedOrders = data.map(order => {
        // Calculate total from order items
        const total = order.order_items?.reduce((sum: number, item: any) => {
          const itemPrice = item.price_at_time || (item.product?.price || 0);
          const itemTotal = (item.quantity || 0) * itemPrice;
          return sum + itemTotal;
        }, 0) || 0;

        // If the order has a linked profile, use that info
        if (order.profiles) {
          return {
            ...order,
            total_amount: total
          };
        }
        
        // If the order is a guest order, create a virtual profile from guest details
        let guestProfile: CustomerProfile | null = null;
        
        if (order.guest_email || order.guest_details) {
          let guestDetails = order.guest_details;
          
          // Parse guest_details if it's a string
          if (guestDetails && typeof guestDetails === 'string') {
            try {
              guestDetails = JSON.parse(guestDetails);
            } catch (e) {
              console.error('Error parsing guest details:', e);
            }
          }
          
          guestProfile = {
            id: 'guest',
            first_name: guestDetails?.firstName || 'Guest',
            last_name: guestDetails?.lastName || '',
            email: order.guest_email || 'No email',
            phone: guestDetails?.phone || '',
            address: guestDetails?.address || ''
          };
        }
        
        return {
          ...order,
          total_amount: total,
          profiles: guestProfile
        };
      });
      
      console.log('Processed orders:', processedOrders);
      setOrders(processedOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Kunde inte hämta beställningar');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditOrder = (orderId: string) => {
    setSelectedOrderId(orderId);
  };

  const handleSaveOrder = () => {
    fetchOrders();
    setSelectedOrderId(null);
  };

  const handleCancelEdit = () => {
    setSelectedOrderId(null);
  };

  // Add escape key handler to close order details
  React.useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && selectedOrderId) {
        handleCancelEdit();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [selectedOrderId]);

  const handleDeleteOrder = async (orderId: string) => {
    setDeleteOrderId(orderId);
  };

  const confirmDeleteOrder = async () => {
    if (!deleteOrderId) return;

    try {
      setIsLoading(true);
      
      // First get all order items to restore inventory
      const { data: orderItems, error: itemsFetchError } = await supabase
        .from('order_items')
        .select('product_id, quantity')
        .eq('order_id', deleteOrderId);

      if (itemsFetchError) {
        console.error('Error fetching order items:', itemsFetchError);
        throw itemsFetchError;
      }

      console.log(`Found ${orderItems?.length || 0} items to process`);

      // Restore inventory for all items
      if (orderItems && orderItems.length > 0) {
        for (const item of orderItems) {
          try {
            console.log(`Processing item: product_id=${item.product_id}, quantity=${item.quantity}`);
            
            // Get current stock level
            const { data: product, error: productError } = await supabase
              .from('products')
              .select('stock_level')
              .eq('id', item.product_id)
              .single();

            if (productError) {
              console.error(`Error fetching product ${item.product_id}:`, productError);
              continue;
            }

            // Calculate new stock level
            const newStockLevel = (product.stock_level || 0) + item.quantity;
            console.log(`Updating stock level for product ${item.product_id} from ${product.stock_level} to ${newStockLevel}`);

            // Update product stock level
            const { error: updateError } = await supabase
              .from('products')
              .update({ stock_level: newStockLevel })
              .eq('id', item.product_id);

            if (updateError) {
              console.error(`Error updating product ${item.product_id}:`, updateError);
            }
          } catch (error) {
            console.error(`Error processing item ${item.product_id}:`, error);
          }
        }
      }

      // Delete related records in order
      console.log('Deleting related records...');

      // 1. Delete order items
      const { error: itemsError } = await supabase
        .from('order_items')
        .delete()
        .eq('order_id', deleteOrderId);
        
      if (itemsError) {
        console.error('Error deleting order items:', itemsError);
        throw itemsError;
      }
      console.log('Order items deleted');

      // 2. Delete order history
      const { error: historyError } = await supabase
        .from('order_history')
        .delete()
        .eq('order_id', deleteOrderId);
        
      if (historyError) {
        console.error('Error deleting order history:', historyError);
        throw historyError;
      }
      console.log('Order history deleted');

      // 3. Delete email queue and logs
      try {
        // Skip attempting to access tables that might not exist
        let skipEmailQueueTables = false;
        
        // Try to delete queue entries directly - if it fails, we'll handle the error gracefully
        console.log('Attempting to process email queue records (if table exists)');
        
        const { data: queueData, error: queueFetchError } = await supabase
          .from('order_email_queue')
          .select('id')
          .eq('order_id', deleteOrderId);
        
        if (queueFetchError) {
          if (queueFetchError.code === '42P01') {
            console.log('order_email_queue table does not exist, skipping this step');
            skipEmailQueueTables = true;
          } else {
            console.error('Error fetching order_email_queue:', queueFetchError);
          }
        }
        
        if (!skipEmailQueueTables && queueData && queueData.length > 0) {
          const queueIds = queueData.map(item => item.id);
          
          // Delete email logs first
          const { error: logsError } = await supabase
            .from('order_email_logs')
            .delete()
            .in('queue_id', queueIds);
            
          if (logsError) {
            console.error('Error deleting order_email_logs:', logsError);
          }
          
          // Then delete queue entries
          const { error: queueError } = await supabase
            .from('order_email_queue')
            .delete()
            .eq('order_id', deleteOrderId);
            
          if (queueError) {
            console.error('Error deleting order_email_queue:', queueError);
          }
          
          console.log('Email queue records processed');
        }
      } catch (error) {
        console.error('Error processing email queue:', error);
        // Continue execution even if there's an error with email queue
      }
      
      // Check for existing email logs before trying to delete
      try {
        // First check if there are any email_logs for this order
        const { data: emailLogsData, error: emailLogsFetchError } = await supabase
          .from('email_logs')
          .select('id, order_id')
          .eq('order_id', deleteOrderId);
        
        if (emailLogsFetchError) {
          console.error('Error checking email_logs:', emailLogsFetchError);
          throw emailLogsFetchError;
        }
        
        console.log(`Found ${emailLogsData?.length || 0} email_logs to delete:`, emailLogsData);
        
        if (emailLogsData && emailLogsData.length > 0) {
          // Attempt to delete each record individually to bypass any bulk operation issues
          for (const log of emailLogsData) {
            console.log(`Attempting to delete email_log with id: ${log.id}`);
            const { error: singleLogDeleteError } = await supabase
              .from('email_logs')
              .delete()
              .eq('id', log.id);
              
            if (singleLogDeleteError) {
              console.error(`Error deleting individual email_log ${log.id}:`, singleLogDeleteError);
              // Continue with other deletions
            } else {
              console.log(`Successfully deleted email_log: ${log.id}`);
            }
          }
          
          // Double-check if all logs were deleted
          const { data: remainingLogs, error: checkError } = await supabase
            .from('email_logs')
            .select('id')
            .eq('order_id', deleteOrderId);
            
          if (checkError) {
            console.error('Error checking remaining email_logs:', checkError);
          } else {
            console.log(`After individual deletions, ${remainingLogs?.length || 0} email_logs remain`);
            
            if (remainingLogs && remainingLogs.length > 0) {
              // Try one more time with a bulk delete
              const { error: bulkDeleteError } = await supabase
                .from('email_logs')
                .delete()
                .eq('order_id', deleteOrderId);
                
              if (bulkDeleteError) {
                console.error('Error with final bulk deletion of email_logs:', bulkDeleteError);
                throw bulkDeleteError;
              } else {
                console.log('All email logs deleted successfully with final attempt');
              }
            } else {
              console.log('All email logs deleted successfully');
            }
          }
        } else {
          console.log('No email logs found for this order');
        }
      } catch (error) {
        console.error('Error handling email_logs deletion:', error);
        throw error;
      }

      // Delete order_metadata
      try {
        console.log('Attempting to delete order_metadata (if table exists)');
        const { error: metadataError } = await supabase
          .from('order_metadata')
          .delete()
          .eq('order_id', deleteOrderId);
        
        if (metadataError) {
          if (metadataError.code === '42P01') {
            console.log('order_metadata table does not exist, skipping');
          } else {
            console.error('Error deleting order_metadata:', metadataError);
          }
        } else {
          console.log('Order metadata deleted successfully');
        }
      } catch (error) {
        console.error('Error handling order_metadata deletion:', error);
        // Continue even if there's an error with metadata
      }

      // Delete order_logs
      try {
        console.log('Attempting to delete order_logs (if table exists)');
        const { error: logsError } = await supabase
          .from('order_logs')
          .delete()
          .eq('order_id', deleteOrderId);
        
        if (logsError) {
          if (logsError.code === '42P01') {
            console.log('order_logs table does not exist, skipping');
          } else {
            console.error('Error deleting order_logs:', logsError);
          }
        } else {
          console.log('Order logs deleted successfully');
        }
      } catch (error) {
        console.error('Error handling order_logs deletion:', error);
        // Continue even if there's an error with logs
      }

      // Also handle order_email_log (singular) which might be different from order_email_logs
      try {
        console.log('Attempting to delete order_email_log (if table exists)');
        const { error: emailLogError } = await supabase
          .from('order_email_log')
          .delete()
          .eq('order_id', deleteOrderId);
        
        if (emailLogError) {
          if (emailLogError.code === '42P01') {
            console.log('order_email_log table does not exist, skipping');
          } else {
            console.error('Error deleting order_email_log:', emailLogError);
          }
        } else {
          console.log('Order email log deleted successfully');
        }
      } catch (error) {
        console.error('Error handling order_email_log deletion:', error);
        // Continue even if there's an error
      }

      // 4. Finally delete the order itself
      try {
        // Log any remaining constraints before trying to delete
        console.log(`Attempting to delete order with ID: ${deleteOrderId}`);
        
        // Run a final check for any remaining references
        const checkRemainingRefsQuery = `
          SELECT table_name, constraint_name 
          FROM information_schema.table_constraints tc 
          JOIN information_schema.constraint_column_usage ccu 
            ON tc.constraint_name = ccu.constraint_name
          WHERE tc.constraint_type = 'FOREIGN KEY' 
          AND ccu.table_name = 'orders' 
          AND ccu.column_name = 'id'
        `;
        
        // We can't run this directly, but we can log the query for debugging purposes
        console.log('Recommended manual check before deletion:', checkRemainingRefsQuery);
        
        // Try to delete the order
        const { error: orderError } = await supabase
          .from('orders')
          .delete()
          .eq('id', deleteOrderId);
          
        if (orderError) {
          console.error('Error deleting order:', orderError);
          
          // Special handling for foreign key constraints
          if (orderError.code === '23503') {
            console.error('Foreign key constraint violation. Check logs for details.');
            const constraintMatch = orderError.message?.match(/constraint "([^"]+)"/);
            if (constraintMatch && constraintMatch[1]) {
              console.error(`The violating constraint is: ${constraintMatch[1]}`);
              toast.error(`Kunde inte radera beställningen: Det finns fortfarande kopplad data. (${constraintMatch[1]})`);
            } else {
              toast.error(`Kunde inte radera beställningen: Det finns fortfarande kopplad data.`);
            }
          } else {
            toast.error(`Kunde inte radera beställningen: ${orderError.message || 'Okänt fel'}`);
          }
          throw orderError;
        }
        
        console.log('Order deleted successfully');
        
        // Update the local state to remove the deleted order
        setOrders(prevOrders => prevOrders.filter(order => order.id !== deleteOrderId));
        
        toast.success('Beställningen har raderats');
      } catch (orderDeleteError: any) {
        console.error('Error during final order deletion:', orderDeleteError);
        if (!orderDeleteError.message?.includes('foreign key constraint')) {
          toast.error(`Kunde inte radera beställningen: ${orderDeleteError.message || 'Okänt fel'}`);
        }
        throw orderDeleteError;
      }
    } catch (error: any) {
      console.error('Error during delete operation:', error);
      toast.error(`Kunde inte radera beställningen: ${error.message || 'Okänt fel'}`);
    } finally {
      setIsLoading(false);
      setDeleteOrderId(null);
    }
  };

  const cancelDeleteOrder = () => {
    setDeleteOrderId(null);
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-blue-100 text-blue-800';
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'preparing':
        return 'bg-yellow-100 text-yellow-800';
      case 'out_for_delivery':
        return 'bg-purple-100 text-purple-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Väntar';
      case 'confirmed':
        return 'Bekräftad';
      case 'preparing':
        return 'Förbereds';
      case 'out_for_delivery':
        return 'Under leverans';
      case 'delivered':
        return 'Levererad';
      case 'cancelled':
        return 'Avbruten';
      default:
        return status;
    }
  };

  const filteredOrders = orders.filter(order => {
    // Return false if any of the filters doesn't match
    
    // Text search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      
      // Check if the text matches any of the searchable fields
      const matchesSearch = 
        // Search in order ID
        order.id.toLowerCase().includes(searchLower) ||
        
        // Search in display ID if available
        (order.display_id?.toString().includes(searchLower) || false) ||
        
        // Search in customer info if profile exists
        (order.profiles && (
          // Full name
          `${order.profiles.first_name || ''} ${order.profiles.last_name || ''}`.toLowerCase().includes(searchLower) ||
          // Email
          (order.profiles.email && order.profiles.email.toLowerCase().includes(searchLower)) ||
          // Phone
          (order.profiles.phone && order.profiles.phone.toLowerCase().includes(searchLower))
        )) ||
        
        // Search in guest email
        (order.guest_email && order.guest_email.toLowerCase().includes(searchLower));
      
      if (!matchesSearch) return false;
    }
    
    return true;
  });

  // Add a function to format address for display
  const formatAddress = (address: any): string => {
    if (!address) return '';
    
    if (typeof address === 'object') {
      const street = address.street || '';
      const postalCode = address.postal_code || '';
      const city = address.city || '';
      
      if (street && postalCode && city) {
        return `${street}, ${postalCode} ${city}`;
      } else if (street) {
        return street;
      }
    }
    
    // If it's a string, just return it
    return typeof address === 'string' ? address : '';
  };

  if (selectedOrderId) {
    return (
      <OrderEditor
        orderId={selectedOrderId}
        onSave={handleSaveOrder}
        onCancel={handleCancelEdit}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="p-4 border-b border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-gray-50">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center space-x-2">
            <Filter size={16} className="text-gray-500" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-md text-sm focus:ring-green-500 focus:border-green-500 py-1.5"
            >
              <option value="all">Alla beställningar</option>
              <option value="pending">Väntar</option>
              <option value="confirmed">Bekräftad</option>
              <option value="preparing">Förbereds</option>
              <option value="out_for_delivery">Under leverans</option>
              <option value="delivered">Levererad</option>
              <option value="cancelled">Avbruten</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <Calendar size={16} className="text-gray-500" />
            <div className="flex flex-col">
              <label htmlFor="upcoming-delivery-date" className="text-xs text-gray-500 mb-1">Kommande leverans</label>
              <select
                id="upcoming-delivery-date"
                value={selectedDeliveryDateFilter}
                onChange={(e) => setSelectedDeliveryDateFilter(e.target.value)}
                className="border border-gray-300 rounded-md text-sm focus:ring-green-500 focus:border-green-500 py-1.5"
              >
                <option value="">Alla datum</option>
                {deliveryDateOptions.map((option, index) => {
                  // Format date as YYYY-MM-DD string for value and display date in Swedish format
                  const dateStr = option.date.toISOString().split('T')[0];
                  const displayDate = new Date(option.date).toLocaleDateString('sv-SE');
                  const label = index === 0 
                    ? `${displayDate} (Nästa)` 
                    : index === 1 
                      ? `${displayDate} (Nästkommande)` 
                      : displayDate;
                  
                  return (
                    <option key={index} value={dateStr}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </div>
            <button 
              onClick={() => setSelectedDeliveryDateFilter('')}
              className={`p-1 rounded-full ${!selectedDeliveryDateFilter ? 'hidden' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
            >
              <X size={14} />
            </button>
          </div>
          
          <div className="flex items-center space-x-2">
            <History size={16} className="text-gray-500" />
            <div className="flex flex-col">
              <label htmlFor="historical-date" className="text-xs text-gray-500 mb-1">Historiskt datum</label>
              <input
                id="historical-date"
                type="date"
                value={historicalDateFilter}
                onChange={(e) => setHistoricalDateFilter(e.target.value)}
                className="border border-gray-300 rounded-md text-sm focus:ring-green-500 focus:border-green-500 py-1.5"
              />
            </div>
            <button 
              onClick={() => setHistoricalDateFilter('')}
              className={`p-1 rounded-full ${!historicalDateFilter ? 'hidden' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
            >
              <X size={14} />
            </button>
          </div>
        </div>
         
        <div className="relative flex-1 md:max-w-xs">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={16} className="text-gray-500" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Sök beställning..."
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
          />
        </div>
      </div>
      
      {isLoading ? (
        <div className="flex justify-center items-center p-12">
          <Loader2 size={30} className="animate-spin text-green-500" />
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="p-12 text-center text-gray-500 bg-gray-50 rounded-md">
          {searchTerm ? "Inga beställningar matchar sökningen" : "Inga beställningar hittades"}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ordernummer
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Kund
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Datum
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Leveransdatum
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Summa
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Åtgärder
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOrders.map((order) => (
                <tr 
                  key={order.id} 
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {order.display_id ? `#${order.display_id}` : order.id.substring(0, 8).toUpperCase()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {order.profiles ? (
                      <>
                        <div className="font-semibold">
                          {order.profiles.first_name || ''} {order.profiles.last_name || ''}
                        </div>
                        <div className="text-gray-500 text-xs mt-1">
                          {order.profiles.email || 'Ingen e-post'}
                        </div>
                      </>
                    ) : (
                      <span className="text-gray-500 italic">Ingen kunddata</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    <SwedishDateDisplay date={order.created_at} format="date" />
                    <div className="text-xs text-gray-500">
                      <SwedishDateDisplay date={order.created_at} format="time" />
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {order.delivery_date ? (
                      <SwedishDateDisplay date={order.delivery_date} format="date" />
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(order.status)}`}>
                      {getStatusLabel(order.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {Number(order.total_amount || 0).toLocaleString('sv-SE', {
                      style: 'currency',
                      currency: 'SEK',
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEditOrder(order.id)}
                      className="text-green-600 hover:text-green-900 mr-4 transition-colors"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDeleteOrder(order.id)}
                      className="text-red-600 hover:text-red-900 transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Delete Order Confirmation Modal */}
      {deleteOrderId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Bekräfta radering
            </h2>
            <p className="text-gray-600 mb-6">
              Är du säker på att du vill radera denna beställning? Denna åtgärd kan inte ångras.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDeleteOrder}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Avbryt
              </button>
              <button
                onClick={confirmDeleteOrder}
                disabled={isLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
              >
                {isLoading ? 'Raderar...' : 'Radera'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {selectedOrderId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-auto">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50 rounded-t-lg">
              <h2 className="text-xl font-bold text-gray-800">
                Redigera beställning
              </h2>
              <button 
                onClick={handleCancelEdit}
                className="p-2 hover:bg-gray-200 rounded-full transition-colors flex items-center gap-1 text-gray-600 hover:text-gray-800"
                title="Stäng och återgå till orderlistan"
              >
                Stäng <X size={20} />
              </button>
            </div>
            <div className="p-6">
              <OrderEditor 
                orderId={selectedOrderId} 
                onSave={handleSaveOrder} 
                onCancel={handleCancelEdit} 
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderList; 